import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { UndoToast } from '../components/UndoToast';

export interface QueuedUndo {
  message: string;
  reverse: () => Promise<void> | void;
  onExpire?: () => Promise<void> | void;
}

interface UndoContextValue {
  queue: (undo: QueuedUndo) => void;
  dismiss: () => void;
}

const UndoContext = createContext<UndoContextValue | undefined>(undefined);

export function UndoProvider({ children }: PropsWithChildren) {
  const [current, setCurrent] = useState<QueuedUndo | null>(null);
  const [busy, setBusy] = useState(false);
  const currentRef = useRef<QueuedUndo | null>(null);
  currentRef.current = current;

  const queue = useCallback((undo: QueuedUndo) => {
    const displaced = currentRef.current;
    if (displaced?.onExpire) void displaced.onExpire();
    setCurrent(undo);
  }, []);

  const expire = useCallback(() => {
    const expiring = currentRef.current;
    if (expiring?.onExpire) void expiring.onExpire();
    setCurrent(null);
  }, []);

  const dismiss = useCallback(() => setCurrent(null), []);

  const onUndo = useCallback(async () => {
    if (!current || busy) return;
    setBusy(true);
    try {
      await current.reverse();
    } finally {
      setBusy(false);
      setCurrent(null);
    }
  }, [current, busy]);

  const value = useMemo(() => ({ queue, dismiss }), [queue, dismiss]);

  return (
    <UndoContext.Provider value={value}>
      {children}
      <UndoToast
        message={current?.message ?? ''}
        visible={!!current}
        onUndo={() => void onUndo()}
        onDismiss={expire}
      />
    </UndoContext.Provider>
  );
}

export function useUndo(): UndoContextValue {
  const ctx = useContext(UndoContext);
  if (!ctx) throw new Error('useUndo must be used within UndoProvider');
  return ctx;
}
